import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UIEvent } from 'react';
import type { UserTrii } from '@trii/types/dist/Users';

import { useEditContactApi } from '../../context/EditContactApiContext';
import { useTranslationContext } from '../../context/TranslationContext';
import { updateEntity } from '../../services/api';
import type { Pagination } from '../../services/api';

type SimplifiedBusiness = {
  businessId: string;
  businessName: string;
};

type Props = {
  baseUrl?: string;
  spaceId?: string;
  contactId?: string;
  initialBusiness?: SimplifiedBusiness | null;
  onBack: () => void;
  onEntityUpdated?: (entity: unknown) => void;
  currentUser?: UserTrii | null;
};

const DEFAULT_PAGINATION: Omit<Pagination, 'filter' | 'currentPage'> = {
  format: 'IBusiness',
  total: 0,
  lastPage: 1,
  perPage: 10,
  order: 'ASC',
  orderColumn: 'Id',
};

const BusinessSelectEditor = ({
  baseUrl,
  spaceId,
  contactId,
  initialBusiness,
  onBack,
  onEntityUpdated,
  currentUser,
}: Props) => {
  const t = useTranslationContext();
  const api = useEditContactApi();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [inputValue, setInputValue] = useState(() => initialBusiness?.businessName ?? '');
  const [hasSearched, setHasSearched] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimer, setIsLoadingTimer] = useState(false);

  const [options, setOptions] = useState<SimplifiedBusiness[]>([]);
  const [selected, setSelected] = useState<SimplifiedBusiness | null>(initialBusiness ?? null);

  const [currentPage, setCurrentPage] = useState(1);
  const [isNextPage, setIsNextPage] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);

  const ownerValue = useMemo(() => {
    const displayName = (currentUser as any)?.display_name;
    const email = (currentUser as any)?.email;
    return String(displayName || email || '').trim();
  }, [currentUser]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setSelected(initialBusiness ?? null);
    setInputValue(initialBusiness?.businessName ?? '');
  }, [initialBusiness]);

  const resetSearchState = () => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setHasSearched(false);
    setAllLoaded(false);
    setCurrentPage(1);
    setIsNextPage(false);
    setOptions([]);
  };

  const mapBusinesses = (businesses: Array<{ id: string; name: string }>) => {
    return businesses
      .filter((b) => Boolean(b?.id))
      .map((b) => ({ businessId: String(b.id), businessName: String(b.name ?? '') }));
  };

  const fetchPage = async ({ query, page }: { query: string; page: number }) => {
    if (!api) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      const pagination: Pagination = {
        ...DEFAULT_PAGINATION,
        currentPage: page,
        filter: [
          {
            value: query,
            column: 'name',
            condition: '',
          },
        ],
      };

      const response = await api.fetchBusinessTableData({ pagination, signal: controller.signal });
      if (controller.signal.aborted) return;

      const next = mapBusinesses((response?.contactos ?? []) as any);
      setHasSearched(true);

      const total = response?.paginacion?.total ?? 0;

      setOptions((prev) => {
        const merged =
          page === 1
            ? next
            : (() => {
                const byId = new Map<string, SimplifiedBusiness>();
                for (const item of prev) byId.set(item.businessId, item);
                for (const item of next) byId.set(item.businessId, item);
                return Array.from(byId.values());
              })();

        if (total && merged.length >= total) {
          setAllLoaded(true);
        }

        return merged;
      });
    } finally {
      setIsLoading(false);
      setIsLoadingTimer(false);
      setIsNextPage(false);
    }
  };

  const handleQueryChanged = (value: string) => {
    resetSearchState();

    if (!value) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setIsLoadingTimer(true);

    timerRef.current = setTimeout(() => {
      void fetchPage({ query: value, page: 1 });
    }, 800);
  };

  const handleScroll = (e: UIEvent<HTMLElement>) => {
    const element = e.target as HTMLElement;
    const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 1;

    if (!bottom) return;
    if (allLoaded) return;
    if (isLoading) return;
    if (!inputValue) return;

    const nextPage = currentPage + 1;
    setIsNextPage(true);
    setCurrentPage(nextPage);
    void fetchPage({ query: inputValue, page: nextPage });
  };

  const handleCreateBusiness = async () => {
    if (!api) return;
    if (!ownerValue) return;

    const name = String(inputValue ?? '').trim();
    if (!name) return;

    setIsLoading(true);
    try {
      const business = {
        name,
        owner: ownerValue,
        imageUrl: '',
        members: [],
        tags: [],
        properties: [],
        address1: {
          street: '',
          city: '',
          state: '',
          zipcode: '',
          country: '',
        },
        address2: {
          street: '',
          city: '',
          state: '',
          zipcode: '',
          country: '',
        },
      };

      const created = await api.createBusiness({ business });
      const createdOption: SimplifiedBusiness = {
        businessId: String((created as any).id ?? ''),
        businessName: String((created as any).name ?? name),
      };

      setOptions([createdOption]);
      setSelected(createdOption);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!baseUrl || !spaceId) return;
    if (!contactId) return;

    const businessId = selected?.businessId ?? '';

    const updated = await updateEntity({
      baseUrl,
      spaceId,
      entityType: 'contact',
      entityId: contactId,
      body: {
        businessId,
      },
    });

    onEntityUpdated?.(updated);
    onBack();
  };

  const noOptionsNode =
    hasSearched && options.length === 0 && inputValue ? (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5 }}
      >
        <Typography variant="body2">{t('global.noOptions')}</Typography>
        <LoadingButton
          size="small"
          variant="contained"
          onClick={handleCreateBusiness}
          loading={isLoading}
          disabled={isLoading || !ownerValue}
        >
          {t('global.createNew')}
        </LoadingButton>
      </Stack>
    ) : (
      <Typography variant="body2">{t('global.noOptions')}</Typography>
    );

  const isSavingDisabled = !contactId || !baseUrl || !spaceId;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={onBack} aria-label={t('global.back')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1">{t('conversations.contactDetails.business')}</Typography>

        <Box sx={{ flex: 1 }} />

        <LoadingButton
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          loading={false}
          disabled={isSavingDisabled}
        >
          {t('global.save')}
        </LoadingButton>
      </Stack>

      <Box sx={{ position: 'relative' }}>
        {isLoading || isLoadingTimer || isNextPage ? (
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </Box>
        ) : null}

        <Autocomplete
          fullWidth
          disablePortal
          value={selected}
          onChange={(event, newValue) => {
            void event;
            setSelected(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(event, newValue, reason) => {
            void event;
            setInputValue(newValue);
            if (reason !== 'input') return;
            handleQueryChanged(newValue);
          }}
          options={options}
          getOptionLabel={(option) => option?.businessName ?? ''}
          isOptionEqualToValue={(opt, val) => opt.businessId === val.businessId}
          noOptionsText={noOptionsNode}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('conversations.contactDetails.business')}
              placeholder={t('global.noValue')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading || isLoadingTimer ? (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{
            sx: {
              maxHeight: 240,
              overflowY: 'auto',
            },
            onScroll: handleScroll,
          }}
        />
      </Box>
    </Box>
  );
};

export default BusinessSelectEditor;
